import { useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

const norm = (v) => (v ?? "").toString().toLowerCase().trim();
const hasAny = (txt, needles) => needles.some((n) => txt.includes(n));

const toYMD = (fecha) => {
  if (!fecha) return null;
  if (typeof fecha === "string") return fecha; // ya viene YYYY-MM-DD
  if (fecha?.toDate) return fecha.toDate().toISOString().slice(0, 10);
  return null;
};

// Regla de conteo:
// - Para CANCELADA/RECHAZADA, manda `estado` (aunque estadoSesion esté "pendiente").
// - Para REALIZADA/COMPLETADA, acepta `estado` o `estadoSesion`.
const classify = (r) => {
  const e = norm(r.estado);       // pendiente/aprobada/realizada/cancelada/rechazada...
  const es = norm(r.estadoSesion); // pendiente/completada/realizada/no_asistio...

  const isCancel =
    hasAny(e, ["cancel", "rechaz"]) ||
    hasAny(es, ["cancel", "rechaz", "no_asist", "inasist"]);

  const isDone =
    hasAny(e, ["realiz"]) ||
    hasAny(es, ["realiz", "complet"]);

  return { isCancel, isDone };
};

export function useWorkerPerformance(workerId, { fromDate, toDate }) {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workerId || !fromDate || !toDate) {
      setStats(null);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const reservasRef = collection(db, "reservas");

        // Mantengo 1 query por profesional (como tú lo tenías) para no meterte
        // en problemas de índices ahora. Filtramos por rango en JS.
        const qReservas = query(reservasRef, where("profesionalId", "==", workerId));
        const snap = await getDocs(qReservas);

        const primeraFechaPorPaciente = {};
        const serviciosMap = {};
        const pacientesSet = new Set();

        let total = 0;
        let completadas = 0; // realizadas/completadas
        let canceladas = 0;  // canceladas + rechazadas (+ no_asistio si lo usas)
        let pendientesAprobadas = 0;

        snap.forEach((docu) => {
          const r = docu.data();

          // Ignorar tentativas/breaks si te llegan (por seguridad)
          if (r?.esTentativa) return;

          const fechaStr = toYMD(r.fecha);
          if (!fechaStr) return;

          // Primera sesión histórica por paciente (para nuevos/recurrentes)
          const pacienteKey = r.uid || r.email || r.rut || null;
          if (pacienteKey) {
            const actual = primeraFechaPorPaciente[pacienteKey];
            if (!actual || fechaStr < actual) primeraFechaPorPaciente[pacienteKey] = fechaStr;
          }

          // Filtrar por rango seleccionado
          if (fechaStr < fromDate || fechaStr > toDate) return;

          total++;

          const { isCancel, isDone } = classify(r);

          if (isCancel) canceladas++;
          else if (isDone) completadas++;
          else if (["pendiente", "aprobada"].includes(norm(r.estado))) pendientesAprobadas++;
          else pendientesAprobadas++; // fallback

          if (pacienteKey) pacientesSet.add(pacienteKey);

          // -------- Servicios --------
          const nombreServicio =
            r.servicio_raw ||
            (r.servicio_categoria && r.servicio_variante
              ? `${r.servicio_categoria} — ${r.servicio_variante}`
              : r.servicio_categoria ||
                r.servicio_variante ||
                "Otro servicio");

          // valor por sesión (lo que me pediste)
          // - planes: normalmente traen precio_por_sesion
          // - no plan: precio_total suele ser el valor de la sesión
          const valorSesion =
            typeof r.precio_por_sesion === "number"
              ? r.precio_por_sesion
              : typeof r.precio_total === "number"
              ? r.precio_total
              : null;

          if (!serviciosMap[nombreServicio]) {
            serviciosMap[nombreServicio] = {
              total: 0,
              realizadas: 0,
              canceladas: 0,
              valorSesion: valorSesion,
            };
          }

          const s = serviciosMap[nombreServicio];
          s.total += 1;
          if (isDone) s.realizadas += 1;
          if (isCancel) s.canceladas += 1;

          // si no tenía valorSesion y ahora sí, lo seteamos
          if (s.valorSesion == null && valorSesion != null) s.valorSesion = valorSesion;
        });

        // Nuevos vs recurrentes
        let pacientesNuevos = 0;
        let pacientesRecurrentes = 0;

        pacientesSet.forEach((key) => {
          const primera = primeraFechaPorPaciente[key];
          if (!primera) return;
          if (primera >= fromDate && primera <= toDate) pacientesNuevos++;
          else pacientesRecurrentes++;
        });

        const denom = completadas + canceladas;
        const tasaAsistencia = denom === 0 ? 0 : Math.round((completadas / denom) * 100);

        const serviciosFrecuentes = Object.entries(serviciosMap)
          .map(([nombre, v]) => ({
            nombre,
            sesiones: v.total,
            realizadas: v.realizadas,
            canceladas: v.canceladas,
            valorSesion: v.valorSesion,
          }))
          // orden: primero por realizadas, luego por total
          .sort((a, b) => (b.realizadas - a.realizadas) || (b.sesiones - a.sesiones));

        if (!cancelled) {
          setStats({
            totalSesiones: total,
            completadas,
            canceladas,
            pendientesAprobadas,
            tasaAsistencia,
            pacientesUnicos: pacientesSet.size,
            pacientesNuevos,
            pacientesRecurrentes,
            serviciosFrecuentes,
            totalRealizadas: completadas, // útil para barras
          });
        }
      } catch (err) {
        console.error("Error en useWorkerPerformance:", err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => (cancelled = true);
  }, [workerId, fromDate, toDate]);

  return { loading, stats, error };
}